import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, AlertTriangle, ArrowUpDown, ArrowDown, ArrowUp, Filter, X, Archive, Table as TableIcon, LayoutGrid, ChevronRight } from "lucide-react";
import AddActionDialog from "@/components/AddActionDialog";
import { formatDate, riskBand } from "@/lib/format";

const STATUSES = ["open", "in_progress", "blocked", "completed"];
const STATUS_COLOR = {
  open: "#1a1a1a",
  in_progress: "#d6a900",
  blocked: "#ff4500",
  completed: "#1a8754",
};

/** iter47 — Priority removed per user; widths tightened; drag-and-drop reordering
 *  persisted per user in localStorage. */
const DEFAULT_COLUMNS = [
  { key: "display_id", label: "ID", width: 80 },
  { key: "title", label: "Title", width: 260 },
  { key: "equipment", label: "Equipment", width: 110 },
  { key: "location", label: "Location", width: 110 },
  { key: "department", label: "Department", width: 110 },
  { key: "type_tag", label: "Type", width: 90 },
  { key: "risk_score", label: "Risk", width: 60 },
  { key: "due_date", label: "Due", width: 100 },
  { key: "assignee_names", label: "Assigned To", width: 150 },
  { key: "status", label: "Status", width: 105 },
];
const COL_STORAGE_KEY_BASE = "actions_col_order_v1";

function _colStorageKey() {
  // iter47 addendum — per-user key so multiple users on one browser don't clobber each other.
  try {
    const raw = localStorage.getItem("opscmd_user");
    const uid = raw ? (JSON.parse(raw).id || "anon") : "anon";
    return `${COL_STORAGE_KEY_BASE}_${uid}`;
  } catch { return COL_STORAGE_KEY_BASE; }
}

function loadColumnOrder() {
  try {
    const raw = localStorage.getItem(_colStorageKey());
    if (!raw) return DEFAULT_COLUMNS;
    const savedKeys = JSON.parse(raw);
    if (!Array.isArray(savedKeys)) return DEFAULT_COLUMNS;
    const byKey = Object.fromEntries(DEFAULT_COLUMNS.map((c) => [c.key, c]));
    const ordered = savedKeys.map((k) => byKey[k]).filter(Boolean);
    // Append any new columns that weren't in the saved order
    for (const c of DEFAULT_COLUMNS) if (!savedKeys.includes(c.key)) ordered.push(c);
    return ordered;
  } catch { return DEFAULT_COLUMNS; }
}

function saveColumnOrder(cols) {
  try { localStorage.setItem(_colStorageKey(), JSON.stringify(cols.map((c) => c.key))); }
  catch { /* ignore */ }
}

export default function Actions() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all"); // all | me
  const [includeArchived, setIncludeArchived] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [sort, setSort] = useState({ key: "due_date", dir: "asc" });
  const [colFilters, setColFilters] = useState({});
  const [columns, setColumns] = useState(loadColumnOrder);
  const [dragKey, setDragKey] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const params = { include_archived: includeArchived };
      if (scope === "me") params.assignee = "me";
      const r = await api.get("/actions", { params });
      setRows(r.data || []);
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [scope, includeArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/users/visible-to-me").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
  }, []);

  const uMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const tMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);

  // Compute assignee names & normalized fields on each row.
  const enriched = useMemo(() => rows.map((a) => {
    const uNames = (a.assigned_user_ids || []).map((id) => uMap[id]?.name || uMap[id]?.email || "").filter(Boolean);
    const tNames = (a.assigned_team_ids || []).map((id) => tMap[id]?.name || "").filter(Boolean);
    const assignee_names = [...uNames, ...tNames].join(", ") || "—";
    const risk = (a.likelihood && a.severity) ? a.likelihood * a.severity : (a.risk_score || null);
    return {
      ...a,
      assignee_names,
      risk_score: risk,
      display_id: a.display_id || a.human_id || "",
    };
  }), [rows, uMap, tMap]);

  // Distinct values per column for Excel-style filter menus.
  const distinct = useMemo(() => {
    const out = {};
    columns.forEach((c) => {
      if (["title", "display_id", "risk_score", "due_date", "assignee_names"].includes(c.key)) return;
      out[c.key] = Array.from(new Set(enriched.map((r) => r[c.key]).filter((v) => v !== null && v !== undefined && v !== "")))
        .sort((a, b) => String(a).localeCompare(String(b)));
    });
    return out;
  }, [enriched, columns]);

  // Apply search + column filters, then sort.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = enriched;
    if (q) {
      out = out.filter((r) =>
        (r.title || "").toLowerCase().includes(q)
        || (r.display_id || "").toLowerCase().includes(q)
        || (r.tags || []).some((t) => (t || "").toLowerCase().includes(q))
      );
    }
    Object.entries(colFilters).forEach(([key, set]) => {
      if (!set || set.size === 0) return;
      out = out.filter((r) => set.has(r[key] ?? ""));
    });
    out = [...out].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const dir = sort.dir === "asc" ? 1 : -1;
      if (av === bv) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return out;
  }, [enriched, search, colFilters, sort]);

  const toggleSort = (key) => {
    setSort((s) => s.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "asc" });
  };

  const setStatus = async (row, status) => {
    // Tasks, V&V items, and Actions have separate PATCH endpoints
    const url = row.kind === "task" ? `/tasks/${row.id}`
      : row.kind === "vv" ? `/vv-items/${row.id}`
      : `/actions/${row.id}`;
    try {
      await api.patch(url, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const activeFilterCount = Object.values(colFilters).reduce((n, s) => n + (s?.size || 0), 0);
  const clearAllFilters = () => setColFilters({});

  return (
    <div className="p-4 lg:p-8 space-y-5">
      {/* Hero */}
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Unified Actions Ledger · audit-logged</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl leading-tight tracking-tight">Actions.</h1>
            <p className="mt-2 text-sm text-white/75 max-w-md">Every task and action in one filterable ledger. Column headers filter Excel-style. Closed items auto-archive after effectiveness is verified.</p>
          </div>
          <button data-testid="actions-add-button" onClick={() => setAddOpen(true)} className="btn-pill btn-pill-primary">
            <Plus className="w-3.5 h-3.5" /> Add action
          </button>
        </div>
      </section>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-[#1a1a1a]/10 rounded-full px-3 py-1.5 shadow-soft">
            <Search className="w-3.5 h-3.5 text-[#1a1a1a]/50" />
            <input data-testid="actions-search" placeholder="Search title, ID or tag…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent outline-none text-sm w-52 placeholder:text-[#1a1a1a]/40" />
          </div>
          <div className="flex bg-white border border-[#1a1a1a]/10 rounded-full p-1 shadow-soft">
            {[["all", "All"], ["me", "Mine"]].map(([k, l]) => (
              <button key={k} data-testid={`actions-scope-${k}`} onClick={() => setScope(k)}
                className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold ${scope === k ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]"}`}>{l}</button>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a] bg-white border border-[#1a1a1a]/10 rounded-full px-3 py-1.5 shadow-soft cursor-pointer">
            <input type="checkbox" data-testid="actions-toggle-archived" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}
              className="accent-[#ff4500]" />
            <Archive className="w-3 h-3" /> Show archived
          </label>
          {activeFilterCount > 0 && (
            <button data-testid="actions-clear-filters" onClick={clearAllFilters}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-[#ff4500] bg-white border border-[#ff4500]/30 rounded-full px-3 py-1.5 shadow-soft hover:bg-[#fff1ea]">
              <X className="w-3 h-3" /> Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
            </button>
          )}
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
          {visible.length} / {enriched.length} row{enriched.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Data table — Excel-style, iter47 sticky header + drag-drop columns + fixed-height scroll box */}
      <div className="bg-white rounded-2xl border border-[#1a1a1a]/10 shadow-soft overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: "560px" }} data-testid="actions-scroll-container">
          <table className="text-sm border-collapse" style={{ width: columns.reduce((s, c) => s + c.width, 0) }}>
            <thead className="sticky top-0 z-20 bg-[#1a1a1a] text-white shadow-md">
              <tr>
                {columns.map((c) => (
                  <th key={c.key}
                    draggable
                    onDragStart={() => setDragKey(c.key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (!dragKey || dragKey === c.key) return;
                      const from = columns.findIndex((x) => x.key === dragKey);
                      const to = columns.findIndex((x) => x.key === c.key);
                      const next = [...columns];
                      const [moved] = next.splice(from, 1);
                      next.splice(to, 0, moved);
                      setColumns(next); saveColumnOrder(next); setDragKey(null);
                    }}
                    onDragEnd={() => setDragKey(null)}
                    data-testid={`col-header-${c.key}`}
                    style={{ width: c.width, minWidth: c.width, maxWidth: c.width }}
                    className={`text-left px-2 py-2 text-[10px] uppercase tracking-[0.18em] font-bold cursor-move select-none ${
                      dragKey === c.key ? "opacity-50" : ""
                    }`}>
                    <ColumnHeader
                      col={c}
                      sort={sort}
                      onSort={() => toggleSort(c.key)}
                      distinct={distinct[c.key]}
                      selected={colFilters[c.key] || new Set()}
                      onFilter={(vals) => setColFilters((f) => ({ ...f, [c.key]: vals }))}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="py-12 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                    {busy ? "Loading…" : "No matching actions"}
                  </td>
                </tr>
              )}
              {visible.map((r) => <Row key={`${r.kind || "action"}-${r.id}`} row={r} columns={columns} onStatus={(s) => setStatus(r, s)} />)}
            </tbody>
          </table>
        </div>
      </div>

      <AddActionDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={load} />
    </div>
  );
}

function ColumnHeader({ col, sort, onSort, distinct, selected, onFilter }) {
  const [menu, setMenu] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const active = sort.key === col.key;
  const SortIcon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  const filterable = Array.isArray(distinct) && distinct.length > 0;
  const filterCount = selected?.size || 0;

  useEffect(() => {
    if (!menu) return;
    const on = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", on);
    return () => document.removeEventListener("mousedown", on);
  }, [menu]);

  const shown = filterable ? distinct.filter((v) => !q || String(v).toLowerCase().includes(q.toLowerCase())) : [];
  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onFilter(next);
  };
  const selectAll = () => onFilter(new Set(distinct));
  const clear = () => onFilter(new Set());

  return (
    <div className="inline-flex items-center gap-1 whitespace-nowrap">
      <button data-testid={`sort-${col.key}`} onClick={onSort} className="inline-flex items-center gap-1 hover:text-[#ff4500]">
        {col.label}
        <SortIcon className="w-3 h-3 opacity-70" />
      </button>
      {filterable && (
        <div className="relative" ref={ref}>
          <button data-testid={`filter-${col.key}`} onClick={() => setMenu((m) => !m)}
            className={`p-0.5 rounded hover:bg-white/20 ${filterCount > 0 ? "text-[#ff5f15]" : "text-white/60"}`}>
            <Filter className="w-3 h-3" />
            {filterCount > 0 && <span className="absolute -top-1 -right-1 bg-[#ff4500] text-white text-[8px] font-bold rounded-full w-3 h-3 flex items-center justify-center">{filterCount}</span>}
          </button>
          {menu && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-white text-[#1a1a1a] rounded-md shadow-lg border border-[#1a1a1a]/15 z-40 normal-case tracking-normal" data-testid={`filter-menu-${col.key}`}>
              <div className="p-2 border-b border-[#1a1a1a]/10 flex gap-1">
                <input placeholder="find…" value={q} onChange={(e) => setQ(e.target.value)}
                  className="flex-1 text-[11px] px-2 py-1 border border-[#1a1a1a]/20 rounded" />
              </div>
              <div className="p-1 flex gap-1 text-[10px] uppercase tracking-wider font-bold border-b border-[#1a1a1a]/10">
                <button onClick={selectAll} className="text-[#1a1a1a] hover:text-[#ff4500] px-1">All</button>
                <button onClick={clear} className="text-[#1a1a1a] hover:text-[#ff4500] px-1">None</button>
              </div>
              <div className="max-h-52 overflow-auto p-1">
                {shown.length === 0 && <div className="text-[11px] text-[#1a1a1a]/50 px-2 py-1">no values</div>}
                {shown.map((v) => (
                  <label key={String(v)} className="flex items-center gap-1.5 px-1.5 py-1 hover:bg-[#f7f5f2] rounded cursor-pointer text-[11px]">
                    <input type="checkbox" checked={selected.has(v)} onChange={() => toggle(v)} className="accent-[#ff4500]" />
                    <span className="truncate">{String(v)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ row, columns, onStatus }) {
  const isLate = row.due_date && new Date(row.due_date) < new Date() && row.status !== "completed";
  const band = riskBand(row.risk_score);
  const to = row.kind === "task" ? "/app/tasks" : (row.kind === "vv" ? `/app/vv/${row.id}` : `/app/actions/${row.id}`);

  const cellFor = (key) => {
    switch (key) {
      case "display_id": return (
        <>
          <Link to={to} className="hover:underline">{row.display_id || "—"}</Link>
          {row.archived && <span className="inline-flex items-center gap-0.5 ml-1 text-[9px] text-[#1a1a1a]/50"><Archive className="w-2.5 h-2.5" /></span>}
        </>
      );
      case "title": return (
        <>
          <Link to={to} data-testid={`action-title-link-${row.id}`} className="font-display font-bold text-sm text-[#1a1a1a] hover:text-[#ff4500] inline-flex items-center gap-1 max-w-full">
            <span className="truncate block" title={row.title}>{row.title}</span>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
          </Link>
          {row.source_run_display_id && <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/40 truncate" title={row.source_item_title}>from {row.source_run_display_id}</div>}
          {row.kind === "task" && <span className="ml-1 text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">· task</span>}
          {row.kind === "vv" && <span className="ml-1 text-[9px] font-mono uppercase tracking-wider text-[#a13ad6] font-bold">· V&V</span>}
        </>
      );
      case "equipment":
      case "location":
      case "department":
      case "type_tag":
        return <span className="text-xs truncate block" title={row[key] || ""}>{row[key] || "—"}</span>;
      case "risk_score": return row.risk_score && band ? (
        <span className="text-[10px] font-display font-black px-1.5 py-0.5 rounded-full text-white" style={{ background: band.color }} title={band.label}>{row.risk_score}</span>
      ) : <span className="text-[#1a1a1a]/40">—</span>;
      case "due_date": return (
        <span className="whitespace-nowrap text-xs">
          {formatDate(row.due_date) || <span className="text-[#1a1a1a]/40">—</span>}
          {isLate && <AlertTriangle className="w-3 h-3 text-[#ff4500] inline ml-1" />}
        </span>
      );
      case "assignee_names": return <span className="text-xs truncate block" title={row.assignee_names}>{row.assignee_names}</span>;
      case "status": return (
        <select data-testid={`row-status-${row.id}`} value={row.status || "open"} onChange={(e) => onStatus(e.target.value)}
          className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border-0 w-full max-w-[95px]"
          style={{ background: `${STATUS_COLOR[row.status] || "#1a1a1a"}15`, color: STATUS_COLOR[row.status] || "#1a1a1a" }}>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      );
      default: return "—";
    }
  };

  return (
    <tr data-testid={`action-row-${row.id}`} className={`border-b border-[#1a1a1a]/08 hover:bg-[#faf8f4] ${row.archived ? "opacity-60" : ""}`}>
      {columns.map((c) => (
        <td key={c.key}
          style={{ width: c.width, minWidth: c.width, maxWidth: c.width }}
          className="px-2 py-1.5 overflow-hidden">
          {cellFor(c.key)}
        </td>
      ))}
    </tr>
  );
}

// Retain for backward-compat imports (was used by nothing critical).
void LayoutGrid; void TableIcon;
